import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, FederatedProvider } from '@cnt-workspace/data-access';
import { Role } from '@curbnturf/entities';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonNote,
  IonSpinner,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { logoApple, logoFacebook, logoGoogle } from 'ionicons/icons';

type Step = 'signIn' | 'newPassword';

/**
 * Sign-in for admins, on cnt-landing's AuthService (Cognito): email/password
 * (including the temporary-password challenge admins get from
 * `auth/temporary-password`) or Google / Facebook / Apple through Cognito's
 * Hosted UI, which returns to `auth/redirect` (AdminAuthRedirectComponent).
 * Replaces @curbnturf/auth-components' login route and social buttons.
 */
@Component({
  selector: 'admin-login',
  standalone: true,
  imports: [FormsModule, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonContent, IonIcon, IonInput, IonItem, IonNote, IonSpinner],
  templateUrl: './admin-login.component.html',
  styles: [
    `
      ion-card {
        max-width: 420px;
        margin: 10vh auto 0;
      }
      .error {
        display: block;
        margin: 12px 0;
        color: var(--ion-color-danger);
      }
      ion-button {
        margin-top: 16px;
      }
      .or {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 20px 0 4px;
        color: var(--ion-color-medium);
        font-size: 14px;
      }
      .or::before,
      .or::after {
        content: '';
        flex: 1;
        border-top: 1px solid var(--ion-color-light-shade);
      }
      .social {
        margin-top: 8px;
      }
    `,
  ],
})
export class AdminLoginComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  step: Step = 'signIn';
  email = '';
  password = '';
  newPassword = '';
  error = '';
  busy = false;

  readonly providers: FederatedProvider[] = ['Google', 'Facebook', 'Apple'];
  readonly providerIcons: Record<FederatedProvider, string> = { Google: 'logo-google', Facebook: 'logo-facebook', Apple: 'logo-apple' };

  constructor() {
    addIcons({ logoApple, logoFacebook, logoGoogle });
  }

  ngOnInit(): void {
    // Subscribed, not a snapshot: after a refused social sign-in, signOut() has
    // already shown this page and the `denied` redirect reuses the same instance.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params.get('denied')) this.error = 'Admin access required. Sign in with an admin account.';
    });
  }

  async signIn(): Promise<void> {
    if (!this.email || !this.password) return;
    this.busy = true;
    this.error = '';
    const result = await this.auth.signIn(this.email, this.password);
    this.busy = false;

    if (result.ok) {
      if (result.user.role !== Role.admin) {
        this.error = 'Admin access required. Sign in with an admin account.';
        await this.auth.signOut('');
        await this.router.navigate(['/auth/login'], { queryParams: { denied: 1 } });
        return;
      }
      await this.router.navigateByUrl(this.route.snapshot.queryParamMap.get('returnto') || '/');
    } else if (result.needsNewPassword) {
      this.step = 'newPassword';
    } else {
      this.error = result.error;
    }
  }

  /** Leaves the app for Cognito's Hosted UI; the guard's `returnto` rides along as `state`. */
  socialSignIn(provider: FederatedProvider): void {
    this.auth.signInWithProvider(provider, this.route.snapshot.queryParamMap.get('returnto') || undefined);
  }

  async setNewPassword(): Promise<void> {
    if (!this.newPassword) return;
    this.busy = true;
    this.error = '';
    const result = await this.auth.completeNewPassword(this.newPassword);
    this.busy = false;
    if (!result.ok) {
      this.error = result.error;
      return;
    }
    // completeNewPassword doesn't start a session — sign in with the new password.
    this.password = this.newPassword;
    this.newPassword = '';
    this.step = 'signIn';
    await this.signIn();
  }
}
